/* eslint no-param-reassign: "off", no-console: "off" */

import download from 'downloadjs';
import { isAfter } from 'date-fns';

const mergeUsageIntoObject = (payload) => {
  const compositeResult = {};
  if (!payload || !payload.length) return compositeResult;
  const usage = payload[0].metrics.find(m => m.metric == 'MT_TOTALUSAGE');
  if (usage && usage.values) {
    usage.values.forEach(element => {
      compositeResult[element.timestamp.replace('T', ' ').replace('Z', '')] = parseInt(element.value, 10);
    });
  }
  return compositeResult;
}
const getDateInUTCFormat = (cd) => {
  let currentDate = cd;
  if (!currentDate) currentDate = new Date();
  if (typeof currentDate === 'string') currentDate = new Date(currentDate);
  const convertedDate = new Date(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate(), currentDate.getUTCHours(), currentDate.getUTCMinutes(), currentDate.getUTCMinutes(), currentDate.getUTCMilliseconds());
  return convertedDate;
}

const getAccountNameFromId = (accountId, accounts) => {
  const account = accounts.find(a => a.id == accountId);
  return account ? account.name : null;
};

const devicesContainCombiner = (devices) => (!!devices.mTennaPlusSerial) || (!!devices.mTennaSelectSerial)
  || (!!devices.antenna2Model) || (!!devices.antenna2Serial)
  || (!!devices.antenna3Model) || (!!devices.antenna3Serial)
  || (!!devices.antenna4Serial) || (!!devices.antenna4Serial)

let providerNetworkStateRetryCount = 0;
const remoteStore = {
  state: {
    // Remotes List
    remotes: [],
    count: 0,
    remotesLoading: false,
    // Detail / Overview
    remote: null,
    remoteLoading: false,
    remoteRefreshing: false,
    // Configuration
    isLoadingProviderState: true,
    // Subscriptions
    remoteHistoricalMetrics: [],
    remoteHistoricalMetricsLoading: false,
    // Metrics
    metricTypes: [],
    // Usage Daily
    remoteSatelliteUsageDaily: [],
    remoteCellularUsageDaily: [],
    remoteSatelliteUsageDailyLoading: false,
    remoteCellularUsageDailyLoading: false,
    // Devices
    remoteDevicesError: null
  },
  mutations: {
    fetchRemotes(state, payload) {
      state.remotes = payload.payload ?? [];
      state.count = payload.count ?? 0;
    },
    fetchRemote(state, payload) {
      state.remote = payload;      
      if (state.remote && state.remote.devices) {
        // check for combiner
        state.remote.devices.isCombiner = devicesContainCombiner(state.remote.devices);
      }
    },
    addRemote(state, payload) {
      state.remote = payload;
      // add to table
      state.remotes.push({
        id: payload.id,
        accountId: payload.accountId,
        name: payload.name,
        satelliteOnline: false,
        cellularOnline: false,
        normalizedName: payload.name.toLowerCase(),
        provisioned: false,
        activated: false,
        devices: {
          modemModel: payload.devices.modemModel,
          modemSerial: payload.devices.modemSerial,
          antennaModel: payload.devices.antennaModel,
          bucModel: payload.devices.bucModel,
          terminalModel: payload.devices.terminalModel
        },
        subscription: {

        }
      });
      // reset device errors
      state.remoteDevicesError = null;
    },
    updateRemote(state, payload) {
      // merge the old with the new instead of atomic overwrite
      state.remote = {
        ...state.remote,
        ...payload.updated
      };

      const idx = state.remotes.findIndex(r => r.id == payload.updated.id);
      if (idx == -1) return;

      // if the updated remote changed accounts, check to see that the user has permission to the account
      const userAccountMatch = payload.rootState.accounts.accounts.find(a => a.id == payload.updated.account.id);
      if (!userAccountMatch) { 
        // user does not have permission to the new account, remove the Remote from the list
        state.remotes.splice(idx, 1); 
        return; 
      }

      state.remotes[idx] = {
        id: payload.updated.id,
        accountId: payload.updated.account.id,
        accountName: getAccountNameFromId(payload.updated.account.id, payload.rootState.accounts.accounts),
        billedAccountId: payload.updated.billedAccount ? payload.updated.billedAccount.id : null,
        name: payload.updated.name,
        satelliteOnline: false,
        cellularOnline: false,
        normalizedName: payload.updated.name.toLowerCase(),
        provisioned: false,
        activated: false,
        devices: {
          modemModel: payload.updated.devices.modemModel,
          modemSerial: payload.updated.devices.modemSerial,
          antennaModel: payload.updated.devices.antennaModel,
          bucModel: payload.updated.devices.bucModel,
          terminalModel: payload.updated.devices.terminalModel
        },
        subscription: {

        }
      };
      state.remotes = JSON.parse(JSON.stringify(state.remotes));
    },
    updateRemoteDevices(state, payload) {
      if (state.remote) {
        state.remote.devices = payload.remoteDevices;
        state.remote.devices.isCombiner = devicesContainCombiner(state.remote.devices);

        if (payload.job) {
          console.info(payload.job); // TODO: do something with the job
        }
      }
    },
    updateRemoteNetworkConfiguration(state, payload) {
      if (state.remote) state.remote.networkConfiguration = payload;
    },
    fetchRemoteHistoricalMetrics(state, payload) {
      state.remoteHistoricalMetrics = payload;
      state.remoteHistoricalMetricsLoading = false;
    },
    fetchRemoteSatelliteUsageDaily(state, payload) {
      state.remoteSatelliteUsageDaily = payload;
      state.remoteSatelliteUsageDailyLoading = false;
    },
    fetchRemoteCellularUsageDaily(state, payload) {
      state.remoteCellularUsageDaily = payload;
      state.remoteCellularUsageDailyLoading = false;
    },
    // Replacing this with Rich state fetch method
    fetchProviderNetworkState(state, payload) {
      if (state.remote.id == payload.id) {
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const prop in payload) {
          state.remote[prop] = payload[prop];
        }
      }
      // clone the object so computed properties to ensure they react to changes
      state.remote = JSON.parse(JSON.stringify(state.remote));
      state.isLoadingProviderState = false;
    }, 
    fetchProviderNetworkRichState(state, payload) {
      if (state.remote.id == payload.id) {
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const prop in payload) {
          state.remote[prop] = payload[prop];
        }
      }
      state.remote = JSON.parse(JSON.stringify(state.remote));
      state.isLoadingProviderState = false;
    },
    setRemoteDevicesError(state, payload) {
      state.remoteDevicesError = payload;
    },
    fetchMetricTypes(state, payload) {
      state.metricTypes = payload;
    },
    fetchMetricProviders(state, providers) {
      state.metricProviders = providers;
    },
    toggleRemoteIsCombiner(state) {
      if (state.remote && state.remote.devices) {
        state.remote.devices.isCombiner = !state.remote.devices.isCombiner;
        state.remote.devices = JSON.parse(JSON.stringify(state.remote.devices));
      }
    },
    remoteSyncInProgress(state, payload) {
      if (state.remote.id == payload.id) {
        state.remote.syncInProgress = payload.isSyncInProgress;
      }
    },
    fetchRemoteGroups(state, payload) {
      if (state.remote) state.remote.groups = payload;
    },
    cancelPendingRemoteTransfer(state) {
      if (state.remote) state.remote.pendingTransferEmail = null;
    }, 
    getDHCPCalculatedField(state, payload) {
      if (state.remote) state.remote.networkConfiguration = payload;
    },
  },
  actions: {
    /**
     * Fetches a list of remotes
     * @param {*} param0 
     * @param {Object} payload - Object containing paging, sorting, filters
     */
    async fetchRemotes({ state, commit }, payload) {
      if (!(payload.bypassState)) state.remotesLoading = true;

      try {
        const res = await fetch(`/v2/remotes?from=${payload.from}&take=${payload.take}&select=${payload.select || ''}&filters=${payload.filters || ''}&sortBy=${payload.sortBy || ''}&sortDirection=${payload.sortDirection || ''}`);
        if (!(payload.bypassState)) state.remotesLoading = false; 
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!(payload.bypassState)) commit('fetchRemotes', data);
        return data;
      } catch (ex) {
        return { isError: true, ex };
      }
    },
    /**
     * Fetches an individual remote
     * @param {*} param0 
     * @param {string} payload - Object containing individual remote Id and option to bypass cache
     */
    async fetchRemote({ state, commit }, payload) {
      state.remoteLoading = true;
      // option to bypass cache if we've just updated a property (Remote Command) and NEED latest values from DB
      if (!payload.bypassCache) {
        // Try to fetch the remote from idxdb first
        try {
          // eslint-disable-next-line no-undef
          const result = await $remoteStore.getItem(payload.id);
          // If a remote is found in idxdb
          if (result != null) {
            // Commit it so the page loads
            commit('fetchRemote', result);
            // Stop the loading
            state.remoteLoading = false;
            // Start the refresh
            state.remoteRefreshing = true;
          }
        } catch (ex) {
          // If there's an error, failover to API
          console.error(ex);
        }
      }

      try {
        const res = await fetch(`/v2/remotes/${payload.id}`);
        state.remoteLoading = false;
        state.remoteRefreshing = false;
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        commit('fetchRemote', data);
        return true;
      } catch (ex) {
        console.error(ex);
        state.remoteLoading = false;
        return false;
      }
    },
    async executeCommand({ dispatch }, payload) {
      try {
        const res = await fetch(`/v2/remotes/${payload.id}/command/${payload.cmd}`, {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        // fetch update Remote metadata
        await dispatch('fetchRemote', { id: payload.id, bypassCache: true });
        await dispatch('fetchProviderNetworkRichState', { id: payload.id });
        // return response result
        return res.ok;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async addRemote({ commit }, payload) {
      commit('setRemoteDevicesError', null);
      try {
        const res = await fetch('/v2/remotes', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) {
          // 400 or 500
          if (res.status >= 400 && res.status < 500) {
            const errorText = await res.text();
            commit('setRemoteDevicesError', errorText);
          }
          return false;
        }
        const data = await res.json(); 
        commit('addRemote', data);
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchRemoteHistoricalMetrics({ commit }, payload) {
      // try from cache first
      try {
        // eslint-disable-next-line no-undef
        const usage = await $usageMonthlyStore.getItem(payload.remoteIds[0]);
        if (usage) commit('fetchRemoteHistoricalMetrics', usage);
      } catch (ex) { 
        console.error('[fetchRemoteHistoricalMetrics] Error encountered while attempting to fetch data from cache: ', ex);
      }

      try {
        const res = await fetch('/v2/remotes/historicalMetrics', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        const response = await res.json();
        commit('fetchRemoteHistoricalMetrics', response);
        return true;
      } catch (ex) {
        console.error('[fetchRemoteHistoricalMetrics] Error: ', ex);
        return false;
      }
    },
    async fetchRemoteUsageDaily({ commit }, payload) {
      // try {
      //   let key = `${payload.remoteIds[0]}_${payload.type}_${payload.startDate}_${payload.endDate}`;
      //   let usage = await $usageDailyStore.getItem(key);
      //   if (usage) commit(`fetchRemote${payload.type}UsageDaily`, usage);
      // } catch (ex) { }

      try {
        const res = await fetch('/v2/remotes/historicalMetrics', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        const response = await res.json();
        switch (payload.type) {
          case 'Satellite':
            commit(`fetchRemote${payload.type}UsageDaily`, response);
            break;
          case 'Cellular':
            commit(`fetchRemote${payload.type}UsageDaily`, response);
            break;
          default:
            return false;
        }
        return true;
      } catch (ex) {
        console.error('[fetchRemoteUsageDaily] Error.');
        console.error(ex);
        return false;
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async fetchRemoteUsageMonthly({}, payload) {
      // try {
      //   let key = `${payload.remoteIds[0]}_${payload.type}_${payload.startDate}_${payload.endDate}`;
      //   let usage = await $usageMonthlyStore.getItem(key);
      //   if (usage) commit(`fetchRemote${payload.type}UsageMonthly`, usage);
      // } catch (ex) { 
      //   console.error(`[fetchRemoteUsageMonthly] Error encountered while attempting to fetch data from cache: `, ex);
      // }

      try {
        const res = await fetch('/v2/remotes/historicalMetrics', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        const response = await res.json();
        const returnObject = { satellite: null, cellular: null };
        switch (payload.type) {
          case 'Satellite':
            returnObject.satellite = mergeUsageIntoObject(response);
            break;
          case 'Cellular':
            returnObject.cellular = mergeUsageIntoObject(response);
            break;
          default:
            break;
        }
        return returnObject;
      } catch (ex) {
        console.error('[fetchRemoteUsageMonthly] Error.');
        console.error(ex);
        return null;
      }
    },
    async fetchProviderNetworkState({ dispatch, state, commit }, payload) {
      try {
        state.isLoadingProviderState = true;
        const res = await fetch(`/v2/remotes/${payload.id}/hubState`);
        if (!res.ok) {
          state.isLoadingProviderState = false;
          throw new Error(await res.text());
        } else {
          if (res.status == 204) throw new Error('Provider network state not found.');
          const response = await res.json();
          response.id = payload.id;
          commit('fetchProviderNetworkState', response);
        }
      } catch (ex) {
        console.error('[fetchProviderNetworkState] Error. Retrying...');
        console.error(ex);
        if (providerNetworkStateRetryCount < 3) {
          dispatch('fetchProviderNetworkState', payload);
          providerNetworkStateRetryCount++;
        }
      }
    },
    async fetchProviderNetworkRichState({ dispatch, state, commit }, payload) {
      try {
        state.isLoadingProviderState = true;
        const res = await fetch(`/v2.2/remotes/${payload.id}/hubState`);
        if (!res.ok) {
          state.isLoadingProviderState = false;
          throw new Error(await res.text());
        } else {
          if (res.status == 204) throw new Error('Provider network state not found.');
          const response = await res.json();
          response.id = payload.id;
          commit('fetchProviderNetworkRichState', response);
        }
      } catch (ex) {
        console.error('[fetchProviderNetworkRichState] Error. Retrying...');
        console.error(ex);
        if (providerNetworkStateRetryCount < 3) {
          dispatch('fetchProviderNetworkRichState', payload);
          providerNetworkStateRetryCount++;
        }
      }
    },
    async transferRemote({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2/remotes/${payload.id}/transfer`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        const updated = await res.json(); 
        commit('updateRemote', { rootState, updated });
        return { isSuccess: true, updatedRemote: updated };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, error: ex.message };
      }
    },
    async cancelPendingRemoteTransfer({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2/remotes/${payload.id}/transfer`, {
          method: 'DELETE',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        const updated = await res.json(); 
        commit('updateRemote', { rootState, updated });
        commit('cancelPendingRemoteTransfer', {});
        return { isSuccess: true, updatedRemote: updated };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, error: ex.message };
      }
    },
    async updateRemote({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2/remotes/${payload.id}`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        const updated = await res.json(); 
        commit('updateRemote', { rootState, updated });
        return updated;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async updateRemoteNetworkConfiguration({ state, commit }, payload) {
      try {
        const res = await fetch(`/v2/remotes/${payload.remoteId}/networkConfig`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) return { isSuccess: false, data: await res.text() };
        const data = await res.json(); 
        // update vuex state
        commit('updateRemoteNetworkConfiguration', data);        
        // update the cache
        // eslint-disable-next-line no-undef
        await $remoteStore.setItem(payload.remoteId, state.remote);
        return { isSuccess: true, data };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, data: ex.message };
      }
    },
    async updateRemoteDevices({ commit }, payload) {
      commit('setRemoteDevicesError', null);
      try {
        const res = await fetch(`/v2/remotes/${payload.id}/devices`, {
          body: JSON.stringify(payload.devices),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) {
          // 400 or 500
          if (res.status >= 400 && res.status < 500) {
            const errorText = await res.text();
            commit('setRemoteDevicesError', errorText);
          }
          return false;
        }
        const data = await res.json(); 
        commit('updateRemoteDevices', data);
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async clearRemoteCache() {
      // eslint-disable-next-line no-undef
      await $remoteStore.clear();
      return true;
    },
    async clearUsageCache() {
      // eslint-disable-next-line no-undef
      await $usageDailyStore.clear();
      // eslint-disable-next-line no-undef
      await $usageMonthlyStore.clear();
      return true;
    },
    // eslint-disable-next-line no-empty-pattern
    async downloadOptionsFile({ }, payload) {
      try {
        const res = await fetch(`/v2/remotes/${payload.id}/downloadOptionsFiles`);
        if (!res.ok) return false;
        const blob = await res.blob();
        const fileName = `${payload.serial || payload.remoteName}-optionsFiles.zip`;
        download(blob, fileName, 'application/zip');
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchMetricProviders({ commit }) {
      try {
        try {
          // eslint-disable-next-line no-undef
          const cachedProviders = $remoteConfigStore.getItem('metricProviders');
          if (cachedProviders && cachedProviders.length) { commit('fetchMetricProviders', cachedProviders); }
        } catch (ex) { console.error(ex); }

        const res = await fetch('/v2/info/metricProviders');
        const providers = await res.json();
        // eslint-disable-next-line no-undef
        $remoteConfigStore.setItem('metricProviders', providers);
        commit('fetchMetricProviders', providers);
        return providers;
      } catch (error) {
        console.error('Unable to fetch metric providers: ', error);
        return false;
      }
    },
    async fetchMetricTypes({ commit }) {
      // retrieve from storage first
      try {
        // eslint-disable-next-line no-undef
        const metrics = $remoteConfigStore.getItem('metrics');
        if (metrics && metrics.length) { commit('fetchMetricTypes', metrics); }
      } catch (ex) { console.error(ex); }

      try {
        const res = await fetch('/v2/info/metrics');
        // check for failed request
        if (!res.ok) throw new Error(await res.text());

        const metrics = await res.json();
        commit('fetchMetricTypes', metrics);
        // eslint-disable-next-line no-undef
        $remoteConfigStore.setItem('metrics', metrics);
        return true;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async syncRemoteToHub({ commit }, payload) {
      try {
        const res = await fetch(`/v2/remotes/${payload.id}/command/syncproviders`, {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        
        if (res.ok) {
          // declare remote syncInProgress
          commit('remoteSyncInProgress', { id: payload.id, isSyncInProgress: true });
          return true;
        }
        return false;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchRemoteGroups({ commit }, payload) {
      try {
        const res = await fetch(`/v2/groups/entities/${payload.entityId}?entityType=${payload.entityType}`);
        if (!res.ok) throw new Error(await res.text());
        const groupsData = await res.json();
        commit('fetchRemoteGroups', groupsData);
        return groupsData;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async getDHCPCalculatedField({ }, payload) {   
      try {
        const res = await fetch('/v2/terminals/networkconfig/dhcp', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) return { isSuccess: false, data: await res.text() };
        const data = await res.json();
        return { isSuccess: true, data };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, data: ex.message };
      }
    }
  },
  getters: {
    remoteHistoricalMetrics: state => mergeUsageIntoObject(state.remoteHistoricalMetrics),
    remoteSatelliteUsageDaily: state => mergeUsageIntoObject(state.remoteSatelliteUsageDaily),
    remoteCellularUsageDaily: state => mergeUsageIntoObject(state.remoteCellularUsageDaily),
    futureSubscriptions: state => {
      if (!state.remote || !state.remote.subscriptions) return null;
      const futureSubscriptions = state.remote.subscriptions.filter(s => s.endReason != 'UserTerminated' && isAfter(new Date(s.startDate), getDateInUTCFormat(new Date())));
      return futureSubscriptions;
    },
    activeSubscription: state => {
      if (!state.remote || !state.remote.subscriptions) return null;
      const active = state.remote.subscriptions.find(s => s.active);
      if (!active) return null;

      return {
        id: active.id,
        name: active.name,
        startDate: active.startDate,
        endDate: active.endDate,
        product: active.productName,
        productShortcode: active.productShortcode,
        region: active.regionName,
        dataPackage: active.dataPackageName,
        dataLimit: active.dataLimitInGb,
        purchaseOrder: active.purchaseOrder,
        billedAccountId: active.billedAccountId,
        isBillable: active.isBillable,
        hasSatellite: active.hasSatellite,
        hasCellular: active.hasCellular,
        canSyncWithProviders: active.canSyncWithProviders,
        canProvision: active.canProvision,
        canActivate: active.canActivate,
        networkIdentifier: active.networkIdentifier,
        autoRenew: active.autoRenew
      }
    }
  }
}
export default remoteStore;
